/** @jsxImportSource @emotion/react */
import React, { memo, useState } from 'react'
import Spinner from '@signal/components/baseline/Spinner'
import UnauthorizedErrorBoundary from '@signal/components/UnauthorizedErrorBoundary'
import isTest from '@signal/utils/env/isTest'
import renderIf from '@signal/utils/renderIf'
import { AnyReactComponentType } from '@signal/utils/types'
import { useHideChromeMode } from '../../hooks'
import {
  PageWrapper,
  ChildWrapper,
  ContentRow,
  LeftWrapper,
  MainWrapper,
  MinWidthWrapper,
  ScrollWrapper
} from './styles'
import { PageLeftPropType, PagePropsType } from './types'

const TESTING_HEADER_RECT = { bottom: 100, top: 0 }
const TESTING_FOOTER_RECT = { bottom: 500, top: 450 }

const PageContent: React.FC<{
  left?: PageLeftPropType
  adjustPageContainerForMenu: boolean
  children: React.ReactNode
}> = ({ left, adjustPageContainerForMenu, children }) => {
  if (left && adjustPageContainerForMenu) {
    return (
      <ContentRow>
        <LeftWrapper>
          {typeof left === 'function'
            ? React.createElement(left as AnyReactComponentType)
            : left}
        </LeftWrapper>
        <MainWrapper>
          <UnauthorizedErrorBoundary>{children}</UnauthorizedErrorBoundary>
        </MainWrapper>
      </ContentRow>
    )
  }

  return <UnauthorizedErrorBoundary>{children}</UnauthorizedErrorBoundary>
}

const Page: React.FC<PagePropsType> = ({
  appMinWidth,
  children,
  footer,
  header,
  defaultHeaderHeight = 0,
  isSkeleton,
  left,
  adjustPageContainerForMenu = false
}) => {
  const hideChrome = useHideChromeMode()
  const [footerHeight, setFooterHeight] = useState(0)
  const [headerHeight, setHeaderHeight] = useState(
    hideChrome ? 0 : defaultHeaderHeight
  )

  const setFooterRef = (ref: HTMLDivElement | null) => {
    if (ref && ref.firstChild) {
      const rect = isTest()
        ? TESTING_FOOTER_RECT
        : (ref.firstChild as HTMLDivElement).getBoundingClientRect()
      setFooterHeight(rect.bottom - rect.top)
    }
  }

  const setHeaderRef = (ref: HTMLDivElement | null) => {
    if (ref && ref.firstChild) {
      const rect = isTest()
        ? TESTING_HEADER_RECT
        : (ref.firstChild as HTMLDivElement).getBoundingClientRect()
      setHeaderHeight(rect.bottom - rect.top)
    }
  }

  return (
    <PageWrapper data-testid="page">
      <div ref={ref => setHeaderRef(ref)}>{header}</div>
      <ChildWrapper
        data-testid="page-children"
        headerFooterHeight={headerHeight + footerHeight}
      >
        {renderIf(
          !isSkeleton,
          renderIf(
            Boolean(children),
            () =>
              renderIf(
                Boolean(appMinWidth),
                () => (
                  <ScrollWrapper
                    appMinWidth={appMinWidth}
                    hideChrome={hideChrome}
                    hideFooter={!footer}
                  >
                    <MinWidthWrapper appMinWidth={appMinWidth}>
                      <PageContent
                        left={left}
                        adjustPageContainerForMenu={adjustPageContainerForMenu}
                      >
                        {children}
                      </PageContent>
                    </MinWidthWrapper>
                  </ScrollWrapper>
                ),
                () => (
                  <PageContent
                    left={left}
                    adjustPageContainerForMenu={adjustPageContainerForMenu}
                  >
                    {children}
                  </PageContent>
                )
              ),
            <Spinner />
          )
        )}
      </ChildWrapper>
      <div ref={ref => setFooterRef(ref)}>{footer}</div>
    </PageWrapper>
  )
}

export default memo(Page)
